// ---------------------------------------------------------------------------
// Strategy profiles — biases on the search bot, never a replacement for it
// ---------------------------------------------------------------------------
//
// A profile shapes the search through four hooks (see candidates.js and
// searchBot.js):
//   styles(view, rng)       extra candidate styles it always puts forward
//   mutate(style, view, rng) a constraint applied to EVERY candidate style
//   weights                  evaluation weight overrides
//   bonus(result, seat, plan, view)  extra score on a simulated result
// The search still samples, simulates and picks the best candidate.
//
// What a player can know about an opposing brood (bots/view.js,
// view.opp.grid.broods and the brood ledger on view.opp.board): its corridor
// and its exact row (the whole 3-cell footprint is drawn on the opponent's
// half from the reveal of the round it was built), whether it is still under
// construction (drawn differently; the ledger carries its progress), its egg
// state and its live drones. The brood hunter aims with exactly that.

import { hexDistance } from '../hexGrid.js'
import { plotSubIndices } from '../subplotGrid.js'
import { baseFootprint } from '../subplotBases.js'
import { basesOf, canAttack } from '../gameLogic.js'

const corridorCells = (col) => [0, 1, 2, 3, 4].flatMap(row => plotSubIndices(col, row))
const myBroods = (view) => view.self.board.flatMap((bf, col) => basesOf(bf).map(b => ({ col, ...b })))
const minDist = (s, cells) => Math.min(...cells.map(c => hexDistance(s, c)))

// ── Early brood ─────────────────────────────────────────────────────────────
// Broods at the first legal chance (the first round a corridor holds two
// veterans that started the round there) — and makes that chance come early:
// with no brood yet, it stacks the round's deploys in one corridor (its widest)
// so the pair is ready next round. Then keeps the brood alive: rockets on the
// structures nearest it, its corridor's drones kept home (never attacking from
// it, deploys sent there), and a brood valued above the default.
const earlyBrood = {
    name: 'early-brood',
    weights: { broodMature: 30, broodBuilding: 24 },
    mutate(style, view) {
        const s = { ...style, name: `${style.name}+early-brood` }
        const mine = myBroods(view)
        if (!mine.length) {
            // First chance: every corridor that can brood does (one brood each).
            s.broodCols = view.self.board.map((bf, c) => ((bf.armyQty || 0) >= 2 && !bf.isAttacking ? c : null)).filter(c => c != null)
            s.broodDeep = true
            s.brood = 0
            if (!s.broodCols.length) {
                // No pair anywhere yet: stack this round's deploys in the corridor
                // closest to one (most veterans), ties to the widest.
                const board = view.self.board
                const nest = board.map((_, c) => c).reduce((a, c) =>
                    ((board[c].armyQty || 0) > (board[a].armyQty || 0) ||
                     ((board[c].armyQty || 0) === (board[a].armyQty || 0) && board[c].maxArmyQty > board[a].maxArmyQty) ? c : a))
                s.deployCols = [nest, nest]
                s.noAttackCols = [...new Set([...(s.noAttackCols ?? []), nest])]
                s.moves = 0
            }
        } else {
            // Keep it alive.
            const home = mine[0].col
            s.brood = 0
            s.noAttackCols = [...new Set([...(s.noAttackCols ?? []), ...mine.map(b => b.col)])]
            s.deployCols = [home, home]
            s.moves = 0
            s.rocketNearCells = baseFootprint(mine[0].centerS) ?? [mine[0].centerS]
            s.rockets = Math.max(1, s.rockets || 0)
        }
        return s
    },
    bonus(result, seat) {
        // Standing broods at the end of the simulated round.
        let v = 0
        for (const bf of result[seat].board) for (const b of basesOf(bf)) v += b.mature ? 20 : 15
        return v
    },
}

// ── Brood hunter ────────────────────────────────────────────────────────────
// Goes after the opponent's broods, constructions first (one destroyed cell
// kills one): for every brood it can see, a candidate attacks from each
// NEIGHBOURING corridor at the cell of that corridor nearest the brood — the
// crater's spread does the rest — and, for a construction, one from the
// brood's own corridor at the brood. (Never a mature brood from its own
// corridor: it fires in that corridor's clash and is gone before the crater
// lands.) Killing a brood scores extra.
function nearestCellIn(col, footprint) {
    const cells = corridorCells(col)
    return cells.reduce((a, b) => (minDist(b, footprint) < minDist(a, footprint) ? b : a))
}
const broodHunter = {
    name: 'brood-hunter',
    styles(view) {
        const out = []
        const broods = [...view.opp.grid.broods].filter(b => !b.spent)
            .sort((a, b) => Number(b.underConstruction) - Number(a.underConstruction))
        for (const b of broods) {
            // A mature brood fires in its own corridor's clash and is gone before
            // that corridor's crater lands: only a neighbouring corridor's
            // crater can reach it. A construction doesn't fire: any of the three.
            const from = b.underConstruction ? [b.col - 1, b.col + 1, b.col] : [b.col - 1, b.col + 1]
            for (const c of from) {
                if (c < 0 || c >= view.self.board.length) continue
                if (!canAttack(view.self.board[c]).valid) continue
                const target = c === b.col ? b.footprint[1] : nearestCellIn(c, b.footprint)
                out.push({
                    name: `hunt-c${b.col + 1}-from-c${c + 1}`,
                    aggression: 0.3, moves: 0, brood: 0, defend: 1, greedyTarget: 1, rockets: 0, plane: false,
                    forceAttackCols: [c], targetAt: (col) => (col === c ? target : null),
                })
            }
        }
        return out
    },
    mutate(style, view) {
        // Any attack next to (or in) a brood's corridor aims at the brood.
        const broods = view.opp.grid.broods.filter(b => !b.spent)
        if (!broods.length) return style
        const prior = style.targetAt
        return {
            ...style,
            targetAt: (col) => {
                // In its own corridor only a construction (a mature one fires
                // and leaves first); from next door, either.
                const b = broods.find(x => x.col === col && x.underConstruction) ?? broods.find(x => Math.abs(x.col - col) === 1)
                if (b) return col === b.col ? b.footprint[1] : nearestCellIn(col, b.footprint)
                return prior ? prior(col) : null
            },
        }
    },
    bonus(result, seat, plan, view) {
        // Broods of theirs the simulated round finished off.
        const other = seat === 'p1' ? 'p2' : 'p1'
        const before = view.opp.grid.broods.filter(b => !b.spent)
        const after = new Set(result[other].board.flatMap(bf => basesOf(bf).map(b => b.centerS)))
        // A brood that fired leaves the ledger too — that's no kill.
        const fired = new Set(result.detail[other].fired.map(f => f.st.footprint[0]))
        let v = 0
        for (const b of before) if (!after.has(b.centerS) && !fired.has(b.centerS)) v += b.underConstruction ? 80 : 50
        return v
    },
}

export const PROFILES = { 'early-brood': earlyBrood, 'brood-hunter': broodHunter }
